import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminAPI } from '../../api/admin.api';
import { useAuth } from '../../auth/AuthContext';
import { formatPrice } from '../../utils/formatPrice';
import Loader from '../../components/common/Loader';
import ImageZoomModal from '../../components/common/ImageZoomModal';
import ProductThumbnail from '../../components/product/ProductThumbnail';
import { getProductImage } from '../../utils/productImage';
import './Orders.css';

const PAYMENT_LABELS = {
  courier: 'Pay with courier',
  installment: 'Installment',
  card: 'Pay by card',
};

const STATUS_LABELS = {
  pending: 'Order received',
  confirmed: 'Customer confirmed',
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const CONFIRMED_VIEW_STATUSES = new Set([
  'confirmed',
  'paid',
  'processing',
]);

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Order received' },
  { value: 'confirmed', label: 'Customer confirmed' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Processing' },
  { value: 'cancelled', label: 'Cancelled' },
];

const getCustomerName = (order) => order.customer?.name || order.user?.name || 'N/A';
const getCustomerPhone = (order) => order.customer?.phone || order.phone || 'N/A';
const getCustomerEmail = (order) => order.customer?.email || order.user?.email || order.email || '';
const getCustomerAddress = (order) => order.customer?.address || order.address || 'N/A';
const getCustomerPersonalNumber = (order) => (
  order.customer?.personalNumber ||
  order.customer?.idNumber ||
  order.knifeVerification?.personalId ||
  order.knifeVerification?.personalNumber ||
  order.personalNumber ||
  order.idNumber ||
  ''
);
const getKnifeVerification = (order) => (
  order.knifeVerification && typeof order.knifeVerification === 'object'
    ? order.knifeVerification
    : null
);
const getOrderItems = (order) => (Array.isArray(order.items) ? order.items : []);
const getOrderId = (order) => order.id || order._id || '';
const getItemSpecialCode = (item) => String(
  item?.specialCode ||
  item?.product?.specialCode ||
  item?.productCode ||
  item?.product?.productCode ||
  item?.code ||
  item?.product?.code ||
  ''
).trim();

const getOrderSpecialCodes = (order) => Array.from(new Set(
  getOrderItems(order).map(getItemSpecialCode).filter(Boolean)
));

const getDeliveryArea = (order) => {
  const area = order.customer?.deliveryCity || order.deliveryCity || order.deliveryArea || '';

  if (area) {
    return area;
  }

  const shippingFee = Number(order.shippingFee || 0);
  if (shippingFee === 6) return 'Tbilisi';
  if (shippingFee === 9) return 'Region';
  if (shippingFee > 0) return 'Not selected';

  return 'N/A';
};

const getThumbnailProduct = (item) => {
  if (!item) {
    return {};
  }

  return {
    ...(item.product || {}),
    title: item.title || item.product?.title || item.product?.name || item.productId || 'Product',
    name: item.title || item.product?.title || item.product?.name || item.productId || 'Product',
    brand: item.brand || item.product?.brand || '',
    category: item.category || item.product?.category || '',
    image: item.image || item.product?.image || '',
    images: Array.isArray(item.images) && item.images.length ? item.images : (item.product?.images || []),
  };
};

const formatOrderItems = (order) => {
  const items = getOrderItems(order);

  if (!items.length) {
    return ['N/A'];
  }

  return items.map((item) => {
    const title = item.title || item.product?.title || item.product?.name || item.productId || 'Product';
    const qty = Number(item.qty || item.quantity || 1);
    const prescription = item.prescription || item.selectedPrescription;
    const parts = [
      qty > 1 ? `x${qty}` : '',
      prescription ? `Prescription ${prescription}` : '',
      getItemSpecialCode(item) ? `Code ${getItemSpecialCode(item)}` : '',
    ].filter(Boolean);

    return parts.length ? `${title} (${parts.join(', ')})` : title;
  });
};

const getOrderDisplayItems = (order) => {
  const items = getOrderItems(order);

  if (!items.length) {
    return [{ key: 'empty', label: 'N/A', product: {} }];
  }

  return items.map((item, index) => ({
    key: `${item.productId || item.id || item._id || item.title || 'item'}-${index}`,
    label: formatOrderItems({ items: [item] })[0],
    product: getThumbnailProduct(item),
  }));
};

const getExportItemOption = (item) => (
  item.prescription ||
  item.selectedPrescription ||
  item.filter ||
  item.selectedFilter ||
  item.option ||
  ''
);

const formatExportProducts = (order) => {
  const items = getOrderItems(order);

  if (!items.length) {
    return 'N/A';
  }

  return items
    .map((item) => {
      const title = item.title || item.product?.title || item.product?.name || item.productId || 'Product';
      const selectedOption = getExportItemOption(item);
      return selectedOption ? `${title} ${selectedOption}` : title;
    })
    .join(', ');
};

const getBoxLabel = (order) => {
  if (!order.box) {
    return 'N/A';
  }

  const label = order.box.label || order.box.name || order.box.id || 'Selected box';
  const price = Number(order.box.price || 0);

  return price > 0 ? `${label} (${formatPrice(price)})` : label;
};

const getPaymentLabel = (order) => {
  const label = PAYMENT_LABELS[order.paymentMethod] || order.paymentMethod || 'N/A';

  if (order.paymentMethod === 'card' && order.card?.last4) {
    return `${label} (${order.card.brand || 'CARD'} ${order.card.last4})`;
  }

  return label;
};

const formatOrderDate = (value) => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLocalDateKey = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const compareDateKeys = (left, right) => left.localeCompare(right);

const getStatusClass = (status) => {
  switch (status) {
    case 'confirmed':
      return 'orders-admin__status orders-admin__status--confirmed';
    case 'delivered':
      return 'orders-admin__status orders-admin__status--delivered';
    case 'shipped':
      return 'orders-admin__status orders-admin__status--shipped';
    case 'processing':
      return 'orders-admin__status orders-admin__status--processing';
    case 'paid':
      return 'orders-admin__status orders-admin__status--paid';
    case 'cancelled':
      return 'orders-admin__status orders-admin__status--cancelled';
    default:
      return 'orders-admin__status orders-admin__status--pending';
  }
};

const getStatusLabel = (status) => STATUS_LABELS[status] || STATUS_LABELS.pending;

const getCancellationSummary = (order) => {
  if (order.status !== 'cancelled') {
    return '';
  }

  const hasCancellationMeta = order.cancelledByUser || order.cancellationSource || order.cancelledBy || order.cancelledAt;
  if (!hasCancellationMeta) {
    return '';
  }

  const actor = order.cancelledByUser || order.cancellationSource === 'customer'
    ? 'Customer'
    : (order.cancelledBy?.role || 'Staff');
  const cancelledAt = formatOrderDate(order.cancelledAt);

  return cancelledAt === 'N/A' ? actor : `${actor} on ${cancelledAt}`;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatExportAmount = (value) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString('en-US')} \u10DA\u10D0\u10E0\u10D8`;
};

const EXPORT_HEADERS = [
  '\u10D2\u10D0\u10D2\u10D6\u10D0\u10D5\u10DC\u10D8\u10E1 \u10D7\u10D0\u10E0\u10D8\u10E6\u10D8',
  '\u10E1\u10D0\u10EE\u10D4\u10DA\u10D8 \u10D3\u10D0 \u10D2\u10D5\u10D0\u10E0\u10D8',
  '\u10E2\u10D4\u10DA\u10D4\u10E4\u10DD\u10DC\u10D8',
  '\u10E0\u10D0\u10D8\u10DD\u10DC\u10D8',
  '\u10DB\u10D8\u10E1\u10D0\u10DB\u10D0\u10E0\u10D7\u10D8',
  '\u10DE\u10D8\u10E0\u10D0\u10D3\u10D8 \u10DC\u10DD\u10DB\u10D4\u10E0\u10D8',
  '\u10D9\u10DD\u10D3\u10D8',
  '\u10D7\u10D0\u10DC\u10EE\u10D0',
  '\u10E8\u10D4\u10DC\u10D8\u10E8\u10D5\u10DC\u10D0',
  '\u10DB\u10D8\u10E2\u10D0\u10DC\u10D0',
];

const formatExportDate = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-GB');
};

const formatExportShipping = (order) => {
  const shippingFee = Number(order.shippingFee || 0);
  return shippingFee > 0 ? String(shippingFee) : '';
};

const formatExportNote = (order) => {
  const boxLabel = getBoxLabel(order);
  const details = [
    formatExportProducts(order),
    getPaymentLabel(order),
    boxLabel !== 'N/A' ? `Box: ${boxLabel}` : '',
    order.note || order.customer?.note || '',
  ].filter(Boolean);

  return details.join(' | ');
};

const buildOrdersWorkbookHtml = (selectedOrders) => {
  const headerRow = EXPORT_HEADERS.map((header) => `
    <th>
      <span>${escapeHtml(header)}</span>
      <span class="filter" aria-hidden="true">&#9662;</span>
    </th>
  `).join('');
  const rows = selectedOrders.map((order) => {
    const cells = [
      formatExportDate(order.createdAt),
      getCustomerName(order),
      getCustomerPhone(order),
      getDeliveryArea(order),
      getCustomerAddress(order),
      getCustomerPersonalNumber(order),
      getOrderSpecialCodes(order).join(', '),
      formatExportAmount(order.total || 0),
      formatExportNote(order),
      formatExportShipping(order),
    ];

    return `<tr>${cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`;
  }).join('');

  return `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8" />
        <style>
          body {
            margin: 0;
            font-family: Arial, sans-serif;
            color: #000000;
          }

          .orders-sheet {
            border-collapse: collapse;
            table-layout: fixed;
            width: 1260px;
          }

          .orders-sheet th,
          .orders-sheet td {
            border: 1px solid #bdbdbd;
            color: #000000;
            padding: 4px 6px;
            font-size: 12px;
            line-height: 1.2;
            mso-number-format: "@";
            vertical-align: middle;
            white-space: normal;
            word-wrap: break-word;
          }

          .orders-sheet th {
            position: relative;
            height: 28px;
            background: #ffffff;
            font-weight: 700;
            text-align: left;
          }

          .orders-sheet td {
            height: 27px;
            background: #f2f2f2;
          }

          .orders-sheet tr:nth-child(even) td {
            background: #ffffff;
          }

          .orders-sheet th:nth-child(1) { width: 125px; }
          .orders-sheet th:nth-child(2) { width: 180px; }
          .orders-sheet th:nth-child(3) { width: 110px; }
          .orders-sheet th:nth-child(4) { width: 80px; }
          .orders-sheet th:nth-child(5) { width: 190px; }
          .orders-sheet th:nth-child(6) { width: 120px; }
          .orders-sheet th:nth-child(7) { width: 135px; }
          .orders-sheet th:nth-child(8) { width: 95px; }
          .orders-sheet th:nth-child(9) { width: 260px; }
          .orders-sheet th:nth-child(10) { width: 72px; }

          .filter {
            float: right;
            width: 18px;
            height: 18px;
            margin-left: 6px;
            border: 1px solid #9ca3af;
            background: #edf1f5;
            color: #4b5563;
            font-size: 10px;
            line-height: 17px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <table class="orders-sheet">
          <thead><tr>${headerRow}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
    </html>
  `;
};

const downloadWorkbook = (selectedOrders) => {
  const workbookHtml = buildOrdersWorkbookHtml(selectedOrders);
  const blob = new Blob([`\ufeff${workbookHtml}`], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const exportDate = new Date().toISOString().slice(0, 10);

  anchor.href = url;
  anchor.download = `sifrifana-orders-${exportDate}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const Orders = () => {
  const { isAdmin, isCoworker, canManageOrderAdminActions } = useAuth();
  const [orders, setOrders] = useState([]);
  const [coworkerStats, setCoworkerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [untilDate, setUntilDate] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [resettingStats, setResettingStats] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [activeView, fromDate, untilDate]);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await adminAPI.getOrders();
      const ordersList = data?.items || data?.orders || data?.data || data || [];
      setOrders(Array.isArray(ordersList) ? ordersList : []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, []);

  const fetchCoworkerStats = useCallback(async () => {
    try {
      const data = await adminAPI.getCoworkerStats();
      setCoworkerStats(data || null);
    } catch (error) {
      console.error('Error fetching coworker stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadWorkspaceData = async () => {
      try {
        await Promise.all([
          fetchOrders(),
          (isAdmin || isCoworker) ? fetchCoworkerStats() : Promise.resolve(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaceData();
  }, [fetchCoworkerStats, fetchOrders, isAdmin, isCoworker]);

  const visibleOrders = useMemo(() => orders.filter((order) => {
    if (activeView === 'confirmed' && !CONFIRMED_VIEW_STATUSES.has(order.status)) {
      return false;
    }

    const orderDate = getLocalDateKey(order.createdAt);

    if (!fromDate && !untilDate) {
      return true;
    }

    if (!orderDate) {
      return false;
    }

    if (fromDate && untilDate) {
      const startDate = compareDateKeys(fromDate, untilDate) <= 0 ? fromDate : untilDate;
      const endDate = compareDateKeys(fromDate, untilDate) <= 0 ? untilDate : fromDate;
      return compareDateKeys(orderDate, startDate) >= 0 && compareDateKeys(orderDate, endDate) <= 0;
    }

    return orderDate === (fromDate || untilDate);
  }), [activeView, fromDate, orders, untilDate]);

  const visibleOrderIds = useMemo(
    () => visibleOrders.map(getOrderId).filter(Boolean),
    [visibleOrders],
  );

  const selectedOrders = useMemo(() => {
    const selectedIds = new Set(selectedOrderIds);
    return orders.filter((order) => selectedIds.has(getOrderId(order)));
  }, [orders, selectedOrderIds]);

  const selectedVisibleCount = visibleOrderIds.filter((id) => selectedOrderIds.includes(id)).length;
  const allVisibleSelected = visibleOrderIds.length > 0 && selectedVisibleCount === visibleOrderIds.length;
  const partiallyVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallyVisibleSelected;
    }
  }, [partiallyVisibleSelected]);

  const handleUpdateStatus = async (orderId, status) => {
    try {
      const data = await adminAPI.updateOrderStatus(orderId, status);
      const updatedOrder = data?.order;

      if (updatedOrder) {
        setOrders((currentOrders) => currentOrders.map((order) => (
          getOrderId(order) === orderId ? updatedOrder : order
        )));
      }

      if (isAdmin || isCoworker) {
        await fetchCoworkerStats();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const handleToggleOrder = (orderId) => {
    setSelectedOrderIds((current) => (
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId]
    ));
  };

  const handleToggleVisibleOrders = () => {
    setSelectedOrderIds((current) => {
      const selectedIds = new Set(current);

      if (allVisibleSelected) {
        visibleOrderIds.forEach((id) => selectedIds.delete(id));
      } else {
        visibleOrderIds.forEach((id) => selectedIds.add(id));
      }

      return Array.from(selectedIds);
    });
  };

  const handleClearDates = () => {
    setFromDate('');
    setUntilDate('');
  };

  const handleDownloadSelected = () => {
    if (!selectedOrders.length) {
      return;
    }

    downloadWorkbook(selectedOrders);
  };

  const handleDeleteSelected = async () => {
    if (!selectedOrderIds.length) {
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedOrderIds.length} selected order${selectedOrderIds.length === 1 ? '' : 's'}?`);

    if (!confirmed) {
      return;
    }

    try {
      setDeleting(true);
      await adminAPI.deleteOrders(selectedOrderIds);
      setSelectedOrderIds([]);
      await fetchOrders();
    } catch (error) {
      console.error('Error deleting orders:', error);
      alert('Failed to delete selected orders');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetCoworkerStats = async () => {
    const confirmed = window.confirm('Reset all coworker monthly bonus points to 0?');

    if (!confirmed) {
      return;
    }

    try {
      setResettingStats(true);
      const data = await adminAPI.resetCoworkerStats();
      setCoworkerStats(data || null);
    } catch (error) {
      console.error('Error resetting coworker stats:', error);
      alert('Failed to reset coworker bonus points');
    } finally {
      setResettingStats(false);
    }
  };

  const handleOpenImage = (product, label) => {
    const image = getProductImage(product);

    if (!image) {
      return;
    }

    const title = label || product.title || product.name || 'Product';
    setZoomImage({ src: image, alt: title, title });
  };

  if (loading) return <Loader />;

  const coworkerScore = coworkerStats?.coworker?.points ?? 0;
  const adminCoworkers = coworkerStats?.coworkers || [];
  const bonusPeriod = coworkerStats?.period || '';

  return (
    <div className="orders-admin">
      <div className="orders-admin__header">
        <div>
          <div className="orders-admin__views" role="tablist" aria-label="Order views">
            <button
              type="button"
              className={`orders-admin__view-button${activeView === 'all' ? ' orders-admin__view-button--active' : ''}`}
              onClick={() => setActiveView('all')}
              aria-pressed={activeView === 'all'}
            >
              Orders
            </button>
            <button
              type="button"
              className={`orders-admin__view-button${activeView === 'confirmed' ? ' orders-admin__view-button--active' : ''}`}
              onClick={() => setActiveView('confirmed')}
              aria-pressed={activeView === 'confirmed'}
            >
              Confirmed orders
            </button>
          </div>
          <p>Product, customer, delivery, payment, and status details in one view.</p>
        </div>
        <span>{visibleOrders.length === orders.length ? `${orders.length} total` : `${visibleOrders.length} shown / ${orders.length} total`}</span>
      </div>

      {isCoworker && (
        <section className="orders-admin__scoreboard orders-admin__scoreboard--single">
          <div className="orders-admin__score-card">
            <span>My monthly bonus score</span>
            <strong>{coworkerScore}</strong>
          </div>
        </section>
      )}

      {isAdmin && adminCoworkers.length > 0 && (
        <>
          <div className="orders-admin__score-head">
            <span>{bonusPeriod ? `Monthly bonus period: ${bonusPeriod}` : 'Monthly bonus period'}</span>
            <button type="button" onClick={handleResetCoworkerStats} disabled={resettingStats}>
              {resettingStats ? 'Resetting...' : 'Reset monthly points'}
            </button>
          </div>
          <section className="orders-admin__scoreboard">
            {adminCoworkers.map((coworker) => (
              <div className="orders-admin__score-card" key={coworker.id}>
                <span>{coworker.name}</span>
                <strong>{coworker.points}</strong>
              </div>
            ))}
          </section>
        </>
      )}

      <div className="orders-admin__toolbar">
        <div className="orders-admin__filters">
          <label>
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </label>
          <label>
            <span>Until</span>
            <input
              type="date"
              value={untilDate}
              onChange={(event) => setUntilDate(event.target.value)}
            />
          </label>
          {(fromDate || untilDate) && (
            <button type="button" className="orders-admin__clear-button" onClick={handleClearDates}>
              Clear
            </button>
          )}
        </div>

        {canManageOrderAdminActions && (
          <div className="orders-admin__bulk">
            <label className="orders-admin__select-all">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allVisibleSelected}
                onChange={handleToggleVisibleOrders}
                disabled={!visibleOrderIds.length}
              />
              <span>Select all</span>
            </label>
            <span>{selectedOrderIds.length} selected</span>
            <button
              type="button"
              className="orders-admin__action-button orders-admin__action-button--danger"
              onClick={handleDeleteSelected}
              disabled={!selectedOrderIds.length || deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              type="button"
              className="orders-admin__action-button"
              onClick={handleDownloadSelected}
              disabled={!selectedOrderIds.length}
            >
              Download
            </button>
          </div>
        )}
      </div>

      <div className="orders-admin__list">
        {Array.isArray(visibleOrders) && visibleOrders.length > 0 ? (
          visibleOrders.map((order, index) => {
            const orderId = getOrderId(order);
            const displayItems = getOrderDisplayItems(order);
            const isSelected = selectedOrderIds.includes(orderId);
            const assignedCoworkerName = order.assignedCoworker?.name || 'Unassigned';
            const cancellationSummary = getCancellationSummary(order);
            const orderCodes = getOrderSpecialCodes(order).join(', ');
            const knifeVerification = getKnifeVerification(order);

            return (
              <article className={`orders-admin__card${canManageOrderAdminActions ? '' : ' orders-admin__card--coworker'}`} key={orderId || `order-${index}`}>
                {canManageOrderAdminActions && (
                  <label className="orders-admin__checkbox" aria-label={`Select order ${orderId || index + 1}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleOrder(orderId)}
                      disabled={!orderId}
                    />
                  </label>
                )}

                <div className="orders-admin__image">
                  <div className="orders-admin__image-list">
                    {displayItems.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className="orders-admin__image-button"
                        onClick={() => handleOpenImage(item.product, item.label)}
                        disabled={!getProductImage(item.product)}
                        aria-label={`Zoom ${item.label} photo`}
                      >
                        <ProductThumbnail product={item.product} size="order" />
                      </button>
                    ))}
                  </div>
                </div>

                <section className="orders-admin__product">
                  <div className="orders-admin__eyebrow">Order</div>
                  <span className="orders-admin__order-id" title={orderId}>{orderId || 'N/A'}</span>
                  <div className="orders-admin__item-list">
                    {displayItems.map((item) => (
                      <strong className="orders-admin__item-title" key={item.key}>{item.label}</strong>
                    ))}
                  </div>
                  <div className="orders-admin__mini-row">
                    <span>Received</span>
                    <strong>{formatOrderDate(order.createdAt)}</strong>
                  </div>
                  <div className="orders-admin__mini-row">
                    <span>Taken by</span>
                    <strong>{assignedCoworkerName}</strong>
                  </div>
                  {orderCodes && (
                    <div className="orders-admin__mini-row">
                      <span>Product code</span>
                      <strong>{orderCodes}</strong>
                    </div>
                  )}
                  {cancellationSummary && (
                    <div className="orders-admin__mini-row orders-admin__mini-row--cancelled">
                      <span>Cancelled by</span>
                      <strong>{cancellationSummary}</strong>
                    </div>
                  )}
                </section>

                <section className="orders-admin__details">
                  <div className="orders-admin__detail">
                    <span>Customer</span>
                    <strong>{getCustomerName(order)}</strong>
                    <small>{getCustomerPhone(order)}</small>
                    {getCustomerEmail(order) && <small>{getCustomerEmail(order)}</small>}
                    {knifeVerification?.personalId && <small>Personal ID: {knifeVerification.personalId}</small>}
                    {knifeVerification?.ageConfirmed && <small>Knife 21+ confirmed</small>}
                  </div>

                  <div className="orders-admin__detail">
                    <span>Delivery</span>
                    <strong>{getDeliveryArea(order)}</strong>
                    <small>{getCustomerAddress(order)}</small>
                    <small>Shipping: {formatPrice(order.shippingFee || 0)}</small>
                  </div>
                </section>

                <section className="orders-admin__details orders-admin__details--payment">
                  <div className="orders-admin__detail">
                    <span>Payment</span>
                    <strong>{getPaymentLabel(order)}</strong>
                    <small>Box: {getBoxLabel(order)}</small>
                  </div>

                  <div className="orders-admin__detail">
                    <span>Total</span>
                    <strong className="orders-admin__total">{formatPrice(order.total || 0)}</strong>
                    <small>Subtotal: {formatPrice(order.subtotal || 0)}</small>
                  </div>
                </section>

                <section className="orders-admin__actions">
                  <span className={getStatusClass(order.status)}>
                    {getStatusLabel(order.status)}
                  </span>
                  <label>
                    <span>Change status</span>
                    <select
                      onChange={(event) => handleUpdateStatus(orderId, event.target.value)}
                      value={order.status || 'pending'}
                      className="orders-admin__select"
                    >
                      {!STATUS_OPTIONS.some((option) => option.value === order.status) && (
                        <option value={order.status || 'pending'} disabled hidden>{getStatusLabel(order.status)}</option>
                      )}
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </section>
              </article>
            );
          })
        ) : (
          <div className="orders-admin__empty">
            {activeView === 'confirmed' ? 'No confirmed orders found' : 'No orders found'}
          </div>
        )}
      </div>
      <ImageZoomModal image={zoomImage} onClose={() => setZoomImage(null)} />
    </div>
  );
};

export default Orders;
